import React, {useCallback, useEffect, useMemo, useState} from "react";
import {useReportUnsavedChanges} from "../../util/UnsavedChanges";
import {Autocomplete, Button, Chip, CircularProgress, FormControlLabel, MenuItem, Paper, Radio,
        RadioGroup, Select, TextField, Tooltip, Typography} from "@mui/material";
import {DeleteOutlineOutlined, WarningAmberOutlined} from "@mui/icons-material";

import {AudienceCandidates, AudienceMember, AudienceType, BlindTrainee, CurriculumItem,
        CurriculumState, FrequencyUnit, TrainableObject, checkAudience, getAudienceCandidates,
        getCurriculum, getTrainableObjects, saveCurriculum} from "../../data/trainingCurriculum";
import {Blue3, Grey2, Grey5, Grey17} from "../../theme/styles";

// The body of a Training Curriculum: what it trains on, how often it repeats, and who owes it.
//
// ON THE RECORD'S OWN PAGE rather than a tab of its own, because this IS the record -- a curriculum with
// no objects and no audience is an empty document, not a record with an empty tab.
//
// EDITED AND SAVED AS A WHOLE, matching the server: the two lists are replaced wholesale when sent, so the
// page holds them and saves them together rather than writing a row per click.

const UNITS: {value: FrequencyUnit; label: string}[] = [
    {value: "DAYS", label: "Days"},
    {value: "WEEKS", label: "Weeks"},
    {value: "MONTHS", label: "Months"},
    {value: "YEARS", label: "Years"},
];

const AUDIENCE_SECTIONS: {type: AudienceType; title: string; blurb: string; key: keyof AudienceCandidates}[] = [
    {type: "Position", title: "Positions to Train", key: "Positions",
     blurb: "Everyone holding one of these positions owes the training."},
    {type: "UserGroup", title: "User Groups to Train", key: "UserGroups",
     blurb: "Everyone in the group owes it."},
    {type: "WorkGroup", title: "Work Groups to Train", key: "WorkGroups",
     blurb: "Everyone in the work group owes it, including through the groups it contains."},
    {type: "AccountUser", title: "Users to Train (if applicable)", key: "AccountUsers",
     blurb: "Named people, beyond whoever the groups above already cover."},
];

function Section({title, children}: {title: string; children: React.ReactNode}) {
    return (
        <div style={{marginTop: 28}}>
            <Typography style={{fontSize: 17, fontWeight: 600, color: Grey17}}>{title}</Typography>
            <div style={{borderBottom: `1px solid ${Grey2}`, marginTop: 8, marginBottom: 16}}/>
            {children}
        </div>
    );
}

export default function TrainingCurriculumPanel({objectRevisionUSID, sessionContext, status}: {
    objectRevisionUSID: string;
    sessionContext: any;
    status?: string | null;
}) {
    const [state, setState] = useState<CurriculumState | null>(null);
    const [items, setItems] = useState<CurriculumItem[]>([]);
    const [audience, setAudience] = useState<AudienceMember[]>([]);
    const [refresh, setRefresh] = useState(false);
    const [count, setCount] = useState<string>("365");
    const [unit, setUnit] = useState<FrequencyUnit>("DAYS");
    // A STRING, and empty means "no due date" -- which is a real choice, not a missing number.
    const [grace, setGrace] = useState<string>("");

    // Counted from the LIVE picker state rather than the saved curriculum, so the warning appears
    // the moment somebody names a person -- not after a save and a reload.
    const [candidates, setCandidates] = useState<AudienceCandidates | null>(null);

    // SERVER-SIDE SEARCH, DEBOUNCED. The picker used to load one capped page and let the browser
    // filter it, so in a realm with more than a page of trainable records the rest were unreachable
    // no matter what somebody typed. Now every keystroke narrows at the source -- after 250ms of
    // quiet, because each search costs the server a visibility check per candidate.
    // THE ANSWER IS ABOUT THE PICKER, NOT ABOUT THE LAST SAVE. Held as state and refreshed from the
    // server whenever the lists change, because the check needs reads only the server can do.
    const [blind, setBlind] = useState<BlindTrainee[]>([]);
    const [checking, setChecking] = useState(false);
    // WHETHER THE ANSWER ON SCREEN IS CURRENT. Keeping the last good answer when a check fails is
    // right -- clearing it would read as "everybody can see everything" -- but doing it SILENTLY
    // renders a stale warning identically to a live one. When the endpoint was missing in prod,
    // that looked exactly like the bug this panel was fixed for, and was reported as such.
    const [stale, setStale] = useState(false);

    const [search, setSearch] = useState("");
    const [searching, setSearching] = useState(false);
    const [trainable, setTrainable] = useState<TrainableObject[]>([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [saved, setSaved] = useState(false);

    const editable = state?.Editable === true;

    const load = useCallback(() => {
        setLoading(true);
        Promise.all([
            getCurriculum(sessionContext, objectRevisionUSID),
            getTrainableObjects(sessionContext),
            getAudienceCandidates(sessionContext),
        ]).then(([current, objects, people]) => {
            setState(current);
            setTrainable(objects);
            setCandidates(people);
            setItems(current.Curriculum?.Items ?? []);
            setAudience(current.Curriculum?.Audience ?? []);
            setRefresh(current.Curriculum?.RefreshPeriodically ?? false);
            setCount(String(current.Curriculum?.FrequencyCount ?? 365));
            setUnit((current.Curriculum?.FrequencyUnit as FrequencyUnit) ?? "DAYS");
            setGrace(current.Curriculum?.GracePeriodDays == null
                ? "" : String(current.Curriculum.GracePeriodDays));
            // Seeded from the read so the panel opens with the answer already on it; the effect
            // below owns it from then on.
            setBlind(current.Curriculum?.BlindTrainees ?? []);
            setError(null);
            // WHAT IS ON SCREEN IS NOW WHAT IS ON THE SERVER, by definition -- every field above was
            // just overwritten from it. Clearing here rather than only after a save also covers the
            // reload that follows a save, and a panel reopened on another revision.
            setTouched(false);
        }).catch(problem => setError(problem?.response?.data?.Cause ?? "Could not read this curriculum."))
          .finally(() => setLoading(false));
    }, [sessionContext, objectRevisionUSID]);

    useEffect(load, [load]);

    // The first page arrives with the record; afterwards this owns `trainable`.
    useEffect(() => {
        let cancelled = false;
        const timer = setTimeout(() => {
            setSearching(true);
            getTrainableObjects(sessionContext, search)
                .then(objects => { if (!cancelled) setTrainable(objects); })
                // A FAILED SEARCH LEAVES THE LAST GOOD LIST rather than emptying the picker, which
                // would read as "there is nothing to train on".
                .catch(() => undefined)
                .finally(() => { if (!cancelled) setSearching(false); });
        }, 250);
        return () => { cancelled = true; clearTimeout(timer); };
    }, [search, sessionContext]);

    // RE-ASKED WHENEVER THE LISTS CHANGE. This used to be read once with the curriculum, which meant
    // it answered for the last SAVE: delete somebody from Users to Train and the warning went on
    // naming them, add somebody and it said nothing about them. Reported by Ed on TC-00002.
    //
    // Debounced for the same reason the object search is: each check costs the server a visibility
    // read per person per record, and somebody assembling an audience produces a burst of changes.
    useEffect(() => {
        let cancelled = false;
        const timer = setTimeout(() => {
            setChecking(true);
            checkAudience(sessionContext, items, audience)
                .then(result => {
                    if (cancelled) return;
                    setBlind(result.BlindTrainees ?? []);
                    setStale(false);
                })
                // A FAILED CHECK LEAVES THE LAST GOOD ANSWER rather than clearing the warning, which
                // would read as "everybody can see everything" -- the one wrong thing to say here.
                // It says so, though: see `stale`.
                .catch(() => { if (!cancelled) setStale(true); })
                .finally(() => { if (!cancelled) setChecking(false); });
        }, 400);
        return () => { cancelled = true; clearTimeout(timer); };
    }, [items, audience, sessionContext]);

    const chosenObjects = useMemo(() => new Set(items.map(one => one.ObjectUSID)), [items]);

    // TOUCHED IS NOT THE SAME AS `saved`, which is the green "Saved." confirmation and is false
    // from the moment the panel opens. Two flags because they answer different questions: "did the
    // last save succeed" and "is there anything to save". Running the Save button off `saved` would
    // have disabled it on a freshly loaded panel and enabled it forever after the first edit.
    const [touched, setTouched] = useState(false);
    const dirty = () => { setSaved(false); setTouched(true); };

    // REPORTED UP so the lifecycle buttons at the top of the record can ask before they act. This
    // panel is the reason that mechanism exists: a curriculum released without pressing Save is
    // RELEASED, current, and trains nobody. See util/UnsavedChanges.
    useReportUnsavedChanges("training-curriculum", "Training Setup", touched);

    const save = async () => {
        setSaving(true);
        setError(null);
        try {
            await saveCurriculum(sessionContext, objectRevisionUSID, {
                RefreshPeriodically: refresh,
                // OMITTED WHEN THE REPEAT IS OFF, so the server clears the interval rather than storing a
                // number nothing repeats on.
                FrequencyCount: refresh ? Number(count) : null,
                FrequencyUnit: refresh ? unit : null,
                // Empty box clears the due date rather than leaving the old one in place, so somebody
                // who decides a curriculum should not have a deadline can actually say so.
                GracePeriodDays: grace.trim() === "" ? null : Number(grace),
                Items: items.map(one => ({ObjectUSID: one.ObjectUSID})),
                Audience: audience.map(one => ({AudienceType: one.AudienceType, AudienceUSID: one.AudienceUSID})),
            });
            setSaved(true);
            setTouched(false);
            load();
        } catch (problem: any) {
            setError(problem?.response?.data?.Cause ?? "Could not save this curriculum.");
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div style={{marginTop: 24}}><CircularProgress size={22}/></div>;
    if (error && !state) {
        return <Typography style={{fontSize: 13, color: "#B42318", marginTop: 24}}>{error}</Typography>;
    }

    // `blind` is state, refreshed live by the effect above. It is NOT derived from `state` here:
    // that read the curriculum as last saved, and the warning outlived the people it named.

    const forType = (type: AudienceType) => audience.filter(one => one.AudienceType === type);

    return (
        <Paper elevation={1} style={{width: "100%", boxSizing: "border-box", padding: "16px 20px 24px",
                                     marginTop: 28}}>
            <div style={{display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16}}>
                <Typography style={{fontSize: 17, fontWeight: 600, color: Grey17}}>Periodic Training</Typography>
                <div style={{display: "flex", alignItems: "center", gap: 10}}>
                    {saved ? <Typography style={{fontSize: 12, color: Grey5}}>Saved.</Typography> : null}
                    {/* NOTHING TO SAVE, NOTHING TO PRESS. Ed: "When a change is made, the save
                        button should activate else it is disabled." It also stops the commonest
                        version of the bug this panel caused -- pressing Save, changing something,
                        and pressing Release believing the first Save covered it. */}
                    <Button size="small" variant="contained"
                            disabled={!editable || saving || !touched} onClick={save}
                            sx={{textTransform: "none", borderRadius: "999px", paddingInline: 3}}>
                        Save
                    </Button>
                </div>
            </div>
            {!editable ? (
                <Typography style={{fontSize: 13, color: Grey5, marginTop: 6}}>
                    This curriculum is {String(status ?? state?.Status ?? "").replace("_", " ").toLowerCase()},
                    so what it trains and who it trains are settled. Revise it to make a new version.
                </Typography>
            ) : null}

            {/* ── what it trains on ───────────────────────────────────────────────────────── */}
            <Section title="Objects to Train on">
                <Autocomplete
                    multiple disabled={!editable}
                    options={trainable.filter(one => !chosenObjects.has(one.ObjectUSID))}
                    // THE SERVER HAS ALREADY FILTERED. Leaving MUI's default filter on would narrow
                    // the narrowed list again against its own idea of a match, so a record the
                    // server returned for "00124" could still vanish from the dropdown.
                    filterOptions={options => options}
                    loading={searching}
                    inputValue={search}
                    onInputChange={(_event, value, reason) => {
                        if (reason !== "reset") setSearch(value);
                    }}
                    getOptionLabel={one => `${one.ObjectID ?? ""} - ${one.Name ?? ""}`
                        + (one.OwnerPositionName ? ` (${one.OwnerPositionName})` : "")}
                    value={[]}
                    onChange={(_event, chosen) => {
                        dirty();
                        setItems(previous => [...previous, ...chosen.map(one => ({
                            ObjectUSID: one.ObjectUSID, ObjectID: one.ObjectID, Name: one.Name,
                            ObjectClassification: one.ObjectClassification,
                            DisplayRevision: one.DisplayRevision, Status: one.Status,
                            TrainingRequired: true, TrainingRequireQuiz: one.TrainingRequireQuiz,
                            OwnerPositionName: one.OwnerPositionName,
                        }))]);
                    }}
                    renderInput={params => (
                        <TextField {...params} size="small"
                                   placeholder={search
                                       ? "No match you can see"
                                       : (trainable.length ? "Type a number or a name..."
                                                           : "No record requires training yet")}
                                   helperText="Any record whose Training switch is on that you can see -- a document, an item, a piece of equipment. Type to search by number or name."/>
                    )}
                />

                {/* ONE ROW PER OBJECT, and the row says what it is rather than only its number: a
                    curriculum is read by somebody deciding whether the plan is right. */}
                <div style={{marginTop: 12, border: `1px solid ${Grey2}`, borderRadius: 8}}>
                    {items.length === 0 ? (
                        <Typography style={{fontSize: 13, color: Grey5, padding: 16}}>
                            Nothing yet. A curriculum with no objects obliges nobody to read anything.
                        </Typography>
                    ) : items.map((one, index) => (
                        <div key={one.ObjectUSID}
                             style={{display: "flex", alignItems: "center", gap: 12, padding: "10px 14px",
                                     borderBottom: index < items.length - 1 ? `1px solid ${Grey2}` : "none"}}>
                            <Typography style={{fontSize: 13, fontWeight: 600, color: Blue3, minWidth: 96}}>
                                {one.ObjectID ?? "—"}
                            </Typography>
                            <div style={{flex: 1, minWidth: 0}}>
                                <Typography style={{fontSize: 14, color: Grey17}}>
                                    {one.Name ?? "—"}
                                </Typography>
                                {/* WHOSE RECORD IT IS. This picker is deliberately not persona-scoped
                                    -- naming a record in a curriculum grants nobody access -- so it can
                                    offer one the author cannot open in any list. Without the owner that
                                    row is unexplainable. */}
                                {one.OwnerPositionName ? (
                                    <Typography style={{fontSize: 11, color: Grey5, marginTop: 1}}>
                                        Owned by {one.OwnerPositionName}
                                    </Typography>
                                ) : null}
                            </div>
                            <Chip size="small" variant="outlined" label={one.ObjectClassification ?? "—"}
                                  sx={{fontSize: 11, height: 22}}/>
                            {one.TrainingRequireQuiz ? (
                                <Chip size="small" label="Quiz" sx={{fontSize: 11, height: 22}}/>
                            ) : null}
                            {/* TRAINING CAN BE SWITCHED OFF ON A RECORD A PLAN STILL NAMES. Shown rather
                                than hidden: the row is why nobody is being asked to read it. */}
                            {one.TrainingRequired === false ? (
                                <Tooltip title="Training has been turned off on this record, so it obliges nobody. Remove it, or turn training back on there.">
                                    <WarningAmberOutlined sx={{fontSize: 18, color: "#B54708"}}/>
                                </Tooltip>
                            ) : null}
                            {editable ? (
                                <Button size="small" color="error"
                                        onClick={() => { dirty(); setItems(previous => previous.filter(x => x.ObjectUSID !== one.ObjectUSID)); }}
                                        sx={{textTransform: "none", minWidth: 0}}>
                                    <DeleteOutlineOutlined sx={{fontSize: 18}}/>
                                </Button>
                            ) : null}
                        </div>
                    ))}
                </div>
            </Section>

            {/* ── how often ───────────────────────────────────────────────────────────────── */}
            <div style={{display: "flex", flexWrap: "wrap", gap: 56, marginTop: 24}}>
                <div>
                    <Typography style={{fontSize: 13, fontWeight: 600, color: Grey17}}>
                        Refresh/Repeat Training Periodically?
                    </Typography>
                    <RadioGroup row value={refresh ? "yes" : "no"}
                                onChange={event => { dirty(); setRefresh(event.target.value === "yes"); }}>
                        <FormControlLabel value="yes" disabled={!editable} control={<Radio size="small"/>} label="Yes"/>
                        <FormControlLabel value="no" disabled={!editable} control={<Radio size="small"/>} label="No"/>
                    </RadioGroup>
                </div>
                {/* THE TWO PARTS STAY TWO. "1 Year" and "365 Days" are the same interval and not the same
                    statement, and the record keeps whichever one somebody chose. */}
                <div style={{opacity: refresh ? 1 : 0.45}}>
                    <Typography style={{fontSize: 13, fontWeight: 600, color: Grey17, marginBottom: 8}}>
                        Training Frequency
                    </Typography>
                    <div style={{display: "flex", gap: 10}}>
                        <TextField size="small" type="number" disabled={!editable || !refresh}
                                   value={count}
                                   onChange={event => { dirty(); setCount(event.target.value); }}
                                   slotProps={{htmlInput: {min: 1, style: {width: 64}}}}/>
                        <Select size="small" value={unit} disabled={!editable || !refresh}
                                onChange={event => { dirty(); setUnit(event.target.value as FrequencyUnit); }}
                                sx={{minWidth: 120}}>
                            {UNITS.map(one => <MenuItem key={one.value} value={one.value}>{one.label}</MenuItem>)}
                        </Select>
                    </div>
                </div>
                {/* THE DEADLINE, AND IT IS OPTIONAL. Leaving it empty is how every curriculum behaved
                    before due dates existed: the training is owed, and nothing ever turns red. The
                    helper text says what the clock counts from, because "30 days" from an unstated
                    moment is not a deadline anybody can act on. */}
                <div>
                    <Typography style={{fontSize: 13, fontWeight: 600, color: Grey17, marginBottom: 8}}>
                        Days to Complete
                    </Typography>
                    <TextField size="small" type="number" disabled={!editable}
                               value={grace} placeholder="No due date"
                               onChange={event => { dirty(); setGrace(event.target.value); }}
                               slotProps={{htmlInput: {min: 1, style: {width: 96}}}}/>
                    <Typography style={{fontSize: 11, color: Grey5, marginTop: 6, maxWidth: 260}}>
                        Counted from whichever came last: the document's release, this curriculum's
                        release, or the day the trainee joined the audience. Leave empty for no due date.
                    </Typography>
                </div>
            </div>

            {/* ── who owes it ─────────────────────────────────────────────────────────────── */}
            <Section title="Trainees Details">
                {/* THE OLD SENTENCE WAS TRUE AND IS NOT ANY MORE. Releasing the curriculum now
                    shares each item with each audience GROUP, precisely so the obligation can be
                    discharged -- a trainee who cannot open a procedure could still take its quiz
                    and sign for having read it. */}
                <Typography style={{fontSize: 13, color: Grey5, marginTop: -8, marginBottom: 16}}>
                    Naming somebody here says they owe this training. When this curriculum is released,
                    everyone named by position or group is given <b>view access</b> to each record above,
                    so they can read what they must train on. You need not belong to any of it yourself.
                </Typography>

                {/* NAMED INDIVIDUALS ARE THE GAP, and the author should meet it here rather than
                    after somebody cannot open a procedure they owe. A count, not names: this picker
                    is deliberately not persona-scoped, so naming who is blind to what would leak
                    through the curriculum what the record lists withhold. */}
                {blind.length === 0 && stale && !checking ? (
                    <Typography style={{fontSize: 12.5, color: Grey5, marginBottom: 16}}>
                        Whether these trainees can read what this curriculum names could not be
                        checked just now. Save and reopen to try again.
                    </Typography>
                ) : null}

                {blind.length > 0 ? (
                    <div style={{display: "flex", alignItems: "flex-start", gap: 8, padding: "10px 14px",
                                 backgroundColor: "#FFFAEB", border: "1px solid #FEDF89",
                                 borderRadius: 8, marginBottom: 16, maxWidth: 760}}>
                        <WarningAmberOutlined sx={{fontSize: 18, color: "#B54708", marginTop: "1px"}}/>
                        <div>
                            {/* NAMED, NOT COUNTED. "2 trainees are named individually" told somebody
                                a mechanism; this tells them who to go and fix. */}
                            <Typography style={{fontSize: 12.5, color: "#8A5A00"}}>
                                These trainees cannot read what this curriculum would oblige them to:
                            </Typography>
                            {blind.map(one => (
                                <Typography key={one.AudienceUSID ?? one.Name ?? ""}
                                            style={{fontSize: 12.5, color: "#8A5A00", marginTop: 3}}>
                                    <b>{one.Name ?? "Someone"}</b> — {one.Records.filter(Boolean).join(", ")}
                                </Typography>
                            ))}
                            <Typography style={{fontSize: 12, color: "#8A5A00", marginTop: 6}}>
                                A record is shared with a position or a group, never with one person, so
                                releasing this will not give them access. Name their position instead, or
                                share the record with them by hand.
                                {checking ? <i> Rechecking…</i>
                                 : stale ? <i> This could not be rechecked just now, so it may not
                                   reflect the trainees above.</i>
                                 : null}
                            </Typography>
                        </div>
                    </div>
                ) : null}

                <div style={{display: "flex", flexWrap: "wrap", gap: 24}}>
                    {AUDIENCE_SECTIONS.map(section => {
                        const options = (candidates?.[section.key] ?? []) as {USID: string; Name: string}[];
                        const chosen = forType(section.type);
                        const unavailable = section.type === "Position"
                            ? candidates?.PositionsUnavailableReason : null;
                        return (
                            <div key={section.type} style={{flex: "1 1 420px", minWidth: 320}}>
                                <Typography style={{fontSize: 13, fontWeight: 600, color: Grey17}}>
                                    {section.title}
                                </Typography>
                                <Autocomplete
                                    multiple disabled={!editable || !!unavailable}
                                    options={options.filter(one =>
                                        !chosen.some(picked => picked.AudienceUSID === one.USID))}
                                    getOptionLabel={one => one.Name}
                                    value={chosen.map(one => ({USID: one.AudienceUSID, Name: one.Name ?? ""}))}
                                    isOptionEqualToValue={(a, b) => a.USID === b.USID}
                                    onChange={(_event, picked) => {
                                        dirty();
                                        setAudience(previous => [
                                            ...previous.filter(one => one.AudienceType !== section.type),
                                            ...picked.map(one => ({AudienceType: section.type,
                                                                   AudienceUSID: one.USID, Name: one.Name})),
                                        ]);
                                    }}
                                    renderInput={params => (
                                        <TextField {...params} size="small" placeholder="Select"
                                                   helperText={unavailable ?? section.blurb}/>
                                    )}
                                    sx={{marginTop: 1}}
                                />
                            </div>
                        );
                    })}
                </div>
            </Section>

            {error ? (
                <Typography style={{fontSize: 13, color: "#B42318", marginTop: 16}}>{error}</Typography>
            ) : null}
        </Paper>
    );
}
